<template>
  <div>
    <h1 class="flex mb-8 font-bold text-3xl group">
      <inertia-link class="flex text-indigo-400 group-hover:text-indigo-600" :href="route('users')">
        <icon name="users" class="w-6 mr-3 fill-indigo-400 group-hover:fill-indigo-600" /> Usuários /
      </inertia-link>
      <span class="flex ml-1 font-medium">
        {{ form.first_name }} {{ form.last_name }}
        <img v-if="user.photo" class="block w-8 h-8 rounded-full ml-4" :src="user.photo" />
      </span>
    </h1>
    <trashed-message v-if="user.deleted_at" class="mb-6" @restore="restore">
      Este usuário foi excluído.
    </trashed-message>
    <div class="bg-white rounded-md shadow overflow-hidden max-w-3xl">
      <form @submit.prevent="update">
        <div class="p-8 -mr-6 -mb-8 flex flex-wrap">
          <text-input v-model="form.first_name" :error="form.errors.first_name" class="pr-6 pb-8 w-full lg:w-1/2" label="Nome" />
          <text-input v-model="form.last_name" :error="form.errors.last_name" class="pr-6 pb-8 w-full lg:w-1/2" label="Sobrenome" />
          <select-input v-model="form.role_id" :error="form.errors.role_id" class="pr-6 pb-8 w-full lg:w-1/2" label="Papel do Usuário">
            <option :value="null" />
            <option v-for="role in roles" :key="role.id" :value="role.id">{{ role.name }}</option>
          </select-input>
          <select-input v-if="showOrganizations" v-model="form.organization_id" :error="form.errors.organization_id" class="pr-6 pb-8 w-full lg:w-1/2" label="Organização">
            <option :value="null" />
            <option v-for="organization in organizations" :key="organization.id" :value="organization.id">{{ organization.name }}</option>
          </select-input>
          <text-input v-model="form.email" :error="form.errors.email" class="pr-6 pb-8 w-full lg:w-1/2" label="Email" />
          <div class="pr-6 pb-8 w-full lg:w-1/2">
            <label>Data de Nascimento:</label><br /><br />
            <datepicker v-model="form.date_of_birth" :language="ptBR" :format="customFormatter" :error="form.errors.date_of_birth" placeholder="Selecione..." />
          </div>
          <select-input v-model="form.genre" :error="form.errors.genre" class="pr-6 pb-8 w-full lg:w-1/2" label="Gênero">
            <option :value="null" />
            <option value="man">Homem</option>
            <option value="woman">Mulher</option>
            <!-- <option value="other">Outro</option> -->
          </select-input>
          <div class="pr-6 pb-8 w-full lg:w-1/2">
            <label class="form-label">Estado:</label>
            <select id="state_id" v-model="form.state_id" class="form-select" @change="getCities()">
              <option :value="null" />
              <option v-for="state in states" :key="state.id" :value="state.id" :selected="form.state_id === state.id">{{ state.name }}</option>
            </select>
          </div>
          <div class="pr-6 pb-8 w-full lg:w-1/2">
            <label class="form-label">Cidade:</label>
            <select id="city_id" v-model="form.city_id" class="form-select">
              <option :value="null" />
              <option v-for="city in cities" :key="city.id" :value="city.id" :selected="form.city_id === city.id">{{ city.name }}</option>
            </select>
          </div>

          <text-input v-model="form.phone" :error="form.errors.phone" class="pr-6 pb-8 w-full lg:w-1/2" label="Fone" />
          <text-input v-model="form.user_code" :error="form.errors.user_code" :disabled="true" class="pr-6 pb-8 w-full lg:w-1/2" label="UserCode" />
          <text-input v-model="form.password" :error="form.errors.password" class="pr-6 pb-8 w-full lg:w-1/2" type="password" autocomplete="new-password" label="Password" />
          <file-input v-model="form.photo" :error="form.errors.photo" class="pr-6 pb-8 w-full lg:w-1/2" type="file" accept="image/*" label="Imagem" />
          <text-input v-model="form.url" :error="form.errors.url" class="pr-6 pb-8 w-full lg:w-1/2" label="URL" />

          <div class="pr-6 pb-3 w-full">
            <label class="form-label">Cidades Visíveis para Pesquisa:</label>

            <multiselect
              v-model="form.visible_cities"
              placeholder="Selecione..."
              label="name"
              track-by="name"
              :options="cities"
              :multiple="true"
              :close-on-select="false"
              :clear-on-select="false"
              :preserve-search="false"
              :preselect-first="false"
            >
              <!-- eslint-disable-next-line vue/no-unused-vars -->
              <template slot="selection" slot-scope="{ values, search, isOpen }">
                <span
                  v-if="values.length"
                  class="multiselect__single"
                >
                  {{ values.length }} opções selecionadas
                </span>
              </template>
            </multiselect>
          </div>
        </div>

        <div class="px-8 py-4 bg-gray-50 border-t border-gray-100 flex items-center">
          <button v-if="!user.deleted_at" class="text-red-600 hover:underline" tabindex="-1" type="button" @click="destroy">Deletar Usuário</button>
          <loading-button :loading="form.processing" class="btn-indigo ml-auto" type="submit">Atualizar Usuário</loading-button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import Icon from '@/Shared/Icon'
import Layout from '@/Shared/Layout'
import TextInput from '@/Shared/TextInput'
import FileInput from '@/Shared/FileInput'
import SelectInput from '@/Shared/SelectInput'
import LoadingButton from '@/Shared/LoadingButton'
import TrashedMessage from '@/Shared/TrashedMessage'
import Datepicker from 'vuejs-datepicker'
import moment from 'moment'
import {ptBR} from 'vuejs-datepicker/dist/locale'
import axios from 'axios'
import Multiselect from 'vue-multiselect'

export default {
  metaInfo() {
    return {
      title: `${this.form.first_name} ${this.form.last_name}`,
    }
  },
  components: {
    FileInput,
    LoadingButton,
    SelectInput,
    TextInput,
    TrashedMessage,
    Datepicker,
    Icon,
    Multiselect,
  },
  layout: Layout,
  props: {
    user: Object,
    roles: Array,
    organizations: Array,
    showOrganizations: Boolean,
  },
  remember: 'form',
  data() {
    return {
      states: null,
      cities: [],
      ptBR: ptBR,
      form: this.$inertia.form({
        _method: 'put',
        organization_id: this.user.organization_id,
        role_id: this.user.role_id,
        first_name: this.user.first_name,
        last_name: this.user.last_name,
        email: this.user.email,
        genre: this.user.genre,
        date_of_birth: this.user.date_of_birth,
        phone: this.user.phone,
        user_code: this.user.user_code,
        state_id: this.user.state_id,
        city_id: this.user.city_id,
        password: null,
        photo: null,
        url: this.user.url,
        visible_cities: this.user.visible_cities,
      }),
    }
  },
  mounted() {
    this.getStates()
    this.getCities()
  },
  methods: {
    customFormatter(date) {
      moment.locale('pt-br')
      return moment(date).format('L')
    },
    update() {
      if (this.user.id === 1) {
        alert('Operação Inválida!')
        window.location.href = '/'
      }

      this.form.post(this.route('users.update', this.user.id).url())
    },
    destroy() {
      if (confirm('Tem certeza de que deseja excluir este usuário?')) {
        this.$inertia.delete(this.route('users.destroy', this.user.id))
      }
    },
    restore() {
      if (confirm('Tem certeza de que deseja restaurar este usuário?')) {
        this.$inertia.put(this.route('users.restore', this.user.id))
      }
    },
    getStates() {
      axios
        .get(this.route('states'))
        .then(response => (this.states = response.data))
    },
    getCities() {
      axios
        .get(this.route('cities'), {
          params: {
            stateId:this.form.state_id,
          },
        })
        .then(response => (this.cities = response.data))
    },
  },
}
</script>
